import { z } from 'zod';
import {
  EXTERNAL_REFERRAL_PARTICIPANT_NAME_MAX_LENGTH,
  EXTERNAL_REFERRAL_PHONE_REGEX,
  EXTERNAL_REFERRAL_EMAIL_REGEX,
  EXTERNAL_REFERRAL_SOURCE_MAX_LENGTH,
} from 'common/referral/external-referral-input';

type FormatMessage = (descriptor: {
  id: string;
  defaultMessage: string;
}) => string;

const requiredString = (message: string) =>
  z.string().trim().min(1, { message });

export const createAddExternalReferralSchema = (
  formatMessage: FormatMessage
) => {
  const currentYear = new Date().getFullYear();

  const requiredFieldMessage = formatMessage({
    id: 'is.input.field_required',
    defaultMessage: 'This field is mandatory',
  });
  const invalidEmailMessage = formatMessage({
    id: 'is.add_external_referral.email_invalid',
    defaultMessage: 'Please enter a valid email address',
  });
  const invalidPhoneMessage = formatMessage({
    id: 'is.add_external_referral.phone_invalid',
    defaultMessage: 'Please enter a valid phone number',
  });
  const nameTooLongMessage = formatMessage({
    id: 'is.add_external_referral.name_too_long',
    defaultMessage: `Please enter at most ${EXTERNAL_REFERRAL_PARTICIPANT_NAME_MAX_LENGTH} characters`,
  });
  const invalidYearMessage = formatMessage({
    id: 'is.add_external_referral.year_invalid',
    defaultMessage: `Please enter a valid year from 1900 to ${currentYear}`,
  });
  const referralSourceTooLongMessage = formatMessage({
    id: 'is.add_external_referral.referral_source_too_long',
    defaultMessage: `Please enter at most ${EXTERNAL_REFERRAL_SOURCE_MAX_LENGTH} characters`,
  });

  const yearSchema = z.coerce
    .number()
    .int({ message: invalidYearMessage })
    .min(1900, { message: invalidYearMessage })
    .max(currentYear, { message: invalidYearMessage });

  return z.object({
    studyId: requiredString(requiredFieldMessage),
    siteId: requiredString(requiredFieldMessage),
    firstName: requiredString(requiredFieldMessage).max(
      EXTERNAL_REFERRAL_PARTICIPANT_NAME_MAX_LENGTH,
      { message: nameTooLongMessage }
    ),
    lastName: requiredString(requiredFieldMessage).max(
      EXTERNAL_REFERRAL_PARTICIPANT_NAME_MAX_LENGTH,
      { message: nameTooLongMessage }
    ),
    telephone: requiredString(requiredFieldMessage).superRefine(
      (value, ctx) => {
        // If there are already issues, don't add another one
        if (ctx.issues.length > 0) return;
        if (!EXTERNAL_REFERRAL_PHONE_REGEX.test(value)) {
          ctx.addIssue({
            code: 'custom',
            message: invalidPhoneMessage,
          });
        }
      }
    ),
    year: z
      .string()
      .trim()
      .refine((value) => value === '' || yearSchema.safeParse(value).success, {
        message: invalidYearMessage,
      }),
    zip: z.string().trim(),
    referralSource: z.string().trim().max(EXTERNAL_REFERRAL_SOURCE_MAX_LENGTH, {
      message: referralSourceTooLongMessage,
    }),
    email: requiredString(requiredFieldMessage).superRefine((value, ctx) => {
      // If there are already issues, don't add another one
      if (ctx.issues.length > 0) return;
      if (!EXTERNAL_REFERRAL_EMAIL_REGEX.test(value)) {
        ctx.addIssue({
          code: 'custom',
          message: invalidEmailMessage,
        });
      }
    }),
  });
};

export type AddExternalReferralFormSchema = ReturnType<
  typeof createAddExternalReferralSchema
>;

export type AddExternalReferralFormValues =
  z.infer<AddExternalReferralFormSchema>;
